import { fetchPage } from './api.js';
import { hydrateAccordions } from './accordions.js';

/**
 * Hydrates admin-inserted "content tabs" blocks inside CMS body HTML.
 *
 * The Body editor stores tabs as:
 *   <div class="page-tabs" data-page-tabs='[{"label":"…","url":"/page/slug"}]'></div>
 *
 * This turns each block into a row of tab buttons + a content panel. The first
 * tab is selected on load; clicking another AJAX-loads that page's body (via
 * the public pages API) into the panel. Loaded content is cached per tab, and
 * is safe to inject because every page body is sanitized server-side on save.
 */

const slugFromUrl = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:^|\/)page\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  const t = String(url).trim();
  return /^[A-Za-z0-9_-]+$/.test(t) ? t : null; // bare slug also accepted
};

export function hydratePageTabs(root) {
  if (!root) return;
  root.querySelectorAll('div[data-page-tabs]').forEach((el) => {
    if (el.dataset.ptHydrated) return;
    el.dataset.ptHydrated = '1';

    let tabs = [];
    try { tabs = JSON.parse(el.getAttribute('data-page-tabs') || '[]'); } catch { tabs = []; }
    tabs = (Array.isArray(tabs) ? tabs : []).filter((t) => t && t.label && t.url);
    if (!tabs.length) { el.style.display = 'none'; return; }

    el.textContent = '';
    el.classList.add('page-tabs');

    const nav = document.createElement('div');
    nav.className = 'page-tabs-nav';
    const panel = document.createElement('div');
    panel.className = 'page-tabs-panel';
    panel.hidden = true;
    const cache = new Map();

    const setPanel = (html) => {
      panel.innerHTML = html;
      // Loaded content may itself contain tabs or collapsible sections.
      hydratePageTabs(panel);
      hydrateAccordions(panel);
    };

    // Select a tab: highlight it, then show the linked page's content.
    const activate = async (btn, t) => {
      nav.querySelectorAll('.page-tab-btn.active').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      panel.hidden = false;

      const slug = slugFromUrl(t.url);
      if (!slug) {
        // Not an internal page — offer the link instead of fetching (CORS/safety).
        panel.textContent = '';
        const p = document.createElement('p');
        p.append('This content opens in a new tab: ');
        const a = document.createElement('a');
        a.href = t.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.textContent = t.label;
        p.appendChild(a);
        panel.appendChild(p);
        return;
      }

      if (cache.has(slug)) { setPanel(cache.get(slug)); return; }
      setPanel('<p class="page-tabs-loading">Loading…</p>');
      try {
        const page = await fetchPage(slug);
        const body = page?.body || '<p>This content is being prepared.</p>';
        cache.set(slug, body);
        if (btn.classList.contains('active')) setPanel(body);
      } catch {
        if (btn.classList.contains('active')) {
          setPanel('<p class="page-tabs-loading">Sorry — this content could not be loaded.</p>');
        }
      }
    };

    tabs.forEach((t) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'page-tab-btn';
      btn.textContent = t.label;
      // One tab is always open, so clicking the active tab is a no-op.
      btn.addEventListener('click', () => {
        if (!btn.classList.contains('active')) activate(btn, t);
      });
      nav.appendChild(btn);
    });

    el.appendChild(nav);
    el.appendChild(panel);

    // First tab selected by default — the strip is never shown empty.
    activate(nav.querySelector('.page-tab-btn'), tabs[0]);
  });
}
